import { DEFAULT_LOCALE, type Locale } from './i18n';

/**
 * Single source of truth for internal page slugs. Per-locale overrides
 * become possible per entry; un-listed locales fall back to DEFAULT_LOCALE.
 *
 * Every internal `href` in `app/` and `components/` should resolve through
 * `routePath()` rather than hardcoding a path string — that's what makes a
 * future rename (like /ai → /ai-mcp) a one-line change here instead of a
 * site-wide find/replace, and what will let per-locale slugs (e.g.
 * de: '/ki-mcp') exist later without touching call sites.
 *
 * `compare*` entries for Dub/Rebrandly/Short.io/TinyURL are listed even
 * though only `/compare/bitly` has a page today — Footer already links to
 * them as placeholders ("pages to follow"); the registry is ready the
 * moment those routes ship.
 */
const ROUTES = {
  home: { en: '/' },
  about: { en: '/about' },
  aiMcp: { en: '/ai-mcp' },
  analytics: { en: '/analytics' },
  changelog: { en: '/changelog' },
  changelogConfirm: { en: '/changelog/confirm' },
  check: { en: '/check' },
  compareBitly: { en: '/compare/bitly' },
  compareDub: { en: '/compare/dub' },
  compareRebrandly: { en: '/compare/rebrandly' },
  compareShortIo: { en: '/compare/short-io' },
  compareTinyurl: { en: '/compare/tinyurl' },
  contact: { en: '/contact' },
  customDomains: { en: '/custom-domains' },
  developers: { en: '/developers' },
  docs: { en: '/docs' },
  dynamicResponses: { en: '/dynamic-responses' },
  integrations: { en: '/integrations' },
  learn: { en: '/learn' },
  linkManagement: { en: '/link-management' },
  /* Token-suffixed light-admin page (`/manage/<admin-token>`); the entry holds
   * the static prefix only — links carrying a real token arrive by email. */
  manage: { en: '/manage' },
  /*
   * German legal slugs are what a German reader searches for ("Impressum",
   * "Datenschutz", "AGB"), so they are real paths rather than a translated
   * label on an English URL. `proxy.ts` rewrites them onto the canonical
   * `/legal/[doc]` route, so there is still one page implementation.
   *
   * No `legalCookies`: the site sets no cookies of its own, the dashboard's
   * localStorage tokens are strictly necessary under §25(2)(2) TDDDG, and the
   * privacy policy covers both in its `storage` section. `proxy.ts` redirects
   * the old path there.
   */
  legalImprint: { en: '/legal/imprint', de: '/rechtliches/impressum' },
  legalPrivacy: { en: '/legal/privacy', de: '/rechtliches/datenschutz' },
  legalTerms: { en: '/legal/terms', de: '/rechtliches/agb' },
  pricing: { en: '/pricing' },
  qrCodes: { en: '/qr-codes' },
  reportAbuse: { en: '/report-abuse' },
  security: { en: '/security' },
  smartRouting: { en: '/smart-routing' },
  solutionsEnterprise: { en: '/solutions/enterprise' },
  solutionsMarketing: { en: '/solutions/marketing' },
  status: { en: '/status' },
} satisfies Record<string, Partial<Record<Locale, string>> & { en: string }>;

export type RouteName = keyof typeof ROUTES;

export const ROUTE_NAMES = Object.keys(ROUTES) as RouteName[];

/**
 * Un-prefixed localized slug — `proxy.ts` adds the locale prefix, exactly as
 * hardcoded links behave today (zero behavioral change).
 *
 * Follow-up seam: call sites currently all use the default `lang` param
 * (components don't thread the active locale through yet). Once they do,
 * passing the real `lang` here is what makes per-locale slugs take effect.
 */
export const routePath = (name: RouteName, lang: Locale = DEFAULT_LOCALE): string => {
  // Widening to the declared shape: most entries are English-only literals, so
  // indexing them by a locale the literal does not carry is a type error
  // without this, even though the `satisfies` clause above permits it.
  const entry: Partial<Record<Locale, string>> & { en: string } = ROUTES[name];
  return entry[lang] ?? entry.en;
};
